import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { AddOn } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import {
  accountingIdColDef,
  actionsColDef,
  AddOnChargesRow,
  addOnTypesColDef,
  costRateColDef,
  descriptionColDef,
  externalIdColDef,
  fuelSurchargeScheduleColDef,
  invoiceRateColDef,
  quantityColDef,
  rateTypeColDef,
} from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import AddOnDialog from './components/AddOnDialog';

interface DialogState {
  addOn?: AddOn;
  isOpen: boolean;
}

interface DeleteDialogState {
  addOn?: AddOn;
  isOpen: boolean;
  isSubmitting: boolean;
}

const AddOnCharges = observer(function AddOnCharges() {
  const { userStore, toasterStore } = useStores();
  const { currencyFormatter } = useCompanyCurrency();
  const { deleteAddOn, getAddOns } = useAddOns();
  const userPermissions = userStore.getPermissions();
  const [dialogState, setDialogState] = useState<DialogState>({ isOpen: false });
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [isDialogBusy, setIsDialogBusy] = useState(false);
  const companyId = userStore.currentCompanies[0].id || userStore.userCompany.id;

  const {
    goToPage: goToAddOnsPage,
    isFetching: isFetchingAddOns,
    pagination,
    reloadCurrentPage: reloadAddOnsPage,
    rows: addOns,
    startOver: startAddOnsOver,
  } = useCursorPagination<AddOn>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      getAddOns(companyId, { cursor, limit, link, signal }),
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) =>
      reportRequestError(error, t('error_messages.add_ons.failed_to_fetch')),
  });

  const isMutating = isDialogBusy || deleteDialogState.isSubmitting;
  const isGridBlocked = isFetchingAddOns || isMutating;

  const openDialog = useCallback((addOn?: AddOn) => {
    setDialogState({ isOpen: true, addOn });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const openDeleteDialog = useCallback((addOn: AddOn) => {
    setDeleteDialogState({ isOpen: true, isSubmitting: false, addOn });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleDeleteAddOn = useCallback(async () => {
    const addOnId = deleteDialogState.addOn?.id;

    if (!addOnId) {
      return;
    }

    try {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: true }));
      await deleteAddOn(companyId, addOnId);
      void reloadAddOnsPage();
      toasterStore.push(
        alert(
          t('administration.rates.add_ons.delete_add_on_success'),
          AlertTypes.success,
        ),
      );
      setDeleteDialogState({ isOpen: false, isSubmitting: false });
    } catch {
      setDeleteDialogState((state) => ({ ...state, isSubmitting: false }));
    }
  }, [
    companyId,
    deleteAddOn,
    deleteDialogState.addOn?.id,
    reloadAddOnsPage,
    toasterStore,
  ]);

  const handleAddOnSaved = useCallback(() => {
    const addOnWasEdited = Boolean(dialogState.addOn?.id);

    if (addOnWasEdited) {
      void reloadAddOnsPage();
    } else {
      void goToAddOnsPage();
    }
  }, [dialogState.addOn?.id, goToAddOnsPage, reloadAddOnsPage]);

  const rows = useMemo(() => {
    return addOns.map((addOn) => {
      const row: AddOnChargesRow = {
        id: String(addOn.id),
        addOn,
        utils: {
          canDeleteAddOn: userPermissions.canDeleteAddOn,
          currencyFormatter,
          onDeleteButtonClick(addOn) {
            openDeleteDialog(addOn);
          },
          onEditButtonClick(addOn) {
            openDialog(addOn);
          },
        },
      };

      return row;
    });
  }, [addOns, openDeleteDialog, openDialog, userPermissions.canDeleteAddOn]);

  const columns = useMemo(
    () => [
      descriptionColDef,
      rateTypeColDef,
      quantityColDef,
      costRateColDef,
      invoiceRateColDef,
      fuelSurchargeScheduleColDef,
      addOnTypesColDef,
      externalIdColDef,
      accountingIdColDef,
      actionsColDef(),
    ],
    [],
  );

  useEffect(() => {
    void startAddOnsOver();
  }, [companyId, startAddOnsOver]);

  return (
    <Box>
      <SettingsPageHeader
        title={t('settings.nav_items.add_ons')}
        description={t('settings.page_descriptions.add_ons')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          data-ui="page-settings__rates__add-on-charges__create-button"
          disabled={isMutating}
          onClick={() => openDialog()}
          startIcon={<Add />}
        >
          {t('administration.rates.add_ons.create_add_on')}
        </SmallButton>
      </Box>
      <DataTable
        id="add-on-charges-grid"
        columns={columns}
        rows={rows}
        loading={isGridBlocked}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isGridBlocked}
            onNavigate={(link) => void goToAddOnsPage(link)}
            pagination={pagination}
          />
        }
      />

      <AddOnDialog
        addOn={dialogState.addOn}
        isOpen={dialogState.isOpen}
        onBusyChange={setIsDialogBusy}
        onClose={closeDialog}
        onSuccess={handleAddOnSaved}
      />
      <ConfirmationDialog
        isBusy={deleteDialogState.isSubmitting}
        isOpen={deleteDialogState.isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteAddOn}
        title={t('administration.rates.add_ons.delete_add_on')}
      >
        <Typography>
          {t('administration.rates.add_ons.delete_add_on_confirmation')}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
});

export default AddOnCharges;
